// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/discovery/themes/ThemeDiscoveryCard.module.css';
import ThemeDiscoveryState, {type DiscoveryTheme} from '@app/features/discovery/state/ThemeDiscoveryState';
import {CheckIcon, DownloadSimpleIcon, PaletteIcon, StarIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface ThemeDiscoveryCardProps {
	readonly theme: DiscoveryTheme;
}

export const ThemeDiscoveryCard: React.FC<ThemeDiscoveryCardProps> = observer(({theme}) => {
	const isApplied = ThemeDiscoveryState.isThemeApplied(theme.id);

	const handleToggleTheme = useCallback(async () => {
		if (isApplied) {
			await ThemeDiscoveryState.deactivateTheme(theme.id);
		} else {
			await ThemeDiscoveryState.applyTheme(theme);
		}
	}, [isApplied, theme]);

	return (
		<div
			className={`${styles.card} ${isApplied ? styles.cardActive : ''}`}
			data-flx={`discovery.themes.card.${theme.id}`}
		>
			<div
				className={styles.paletteHeader}
				style={{
					background: `linear-gradient(135deg, ${theme.previewColors[0]} 0%, ${theme.previewColors[1]} 100%)`,
				}}
			>
				{theme.previewColors.map((color, idx) => (
					<div
						key={idx}
						className={styles.swatch}
						style={{backgroundColor: color}}
						title={`Color token: ${color}`}
					/>
				))}
			</div>

			<div className={styles.content}>
				<div className={styles.titleRow}>
					<h3 className={styles.name}>{theme.name}</h3>
					<span className={styles.categoryBadge}>{theme.category}</span>
				</div>

				<span className={styles.author}>by {theme.author}</span>
				<p className={styles.description}>{theme.description}</p>

				<div className={styles.footer}>
					<div className={styles.statsRow}>
						<StarIcon size={14} weight="fill" color="#f59e0b" />
						<span>{theme.rating.toFixed(1)}</span>
						<span style={{margin: '0 4px'}}>•</span>
						<DownloadSimpleIcon size={14} />
						<span>{theme.downloads.toLocaleString()}</span>
					</div>

					<button
						type="button"
						className={`${styles.applyBtn} ${isApplied ? styles.activeBtn : ''}`}
						onClick={handleToggleTheme}
						data-flx={`discovery.themes.apply-btn.${theme.id}`}
					>
						{isApplied ? (
							<>
								<CheckIcon size={15} weight="bold" />
								<span>Applied</span>
							</>
						) : (
							<>
								<PaletteIcon size={15} weight="bold" />
								<span>Apply</span>
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
});
